/**
 * SlotLinkQR - QR code and join code display for linking a player slot
 * 
 * Shows:
 * - QR code encoding the join URL
 * - 4-digit numeric code for manual entry
 * - Expiry countdown
 * - Status updates when slot is claimed
 */

import { useState, useEffect, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { X, RefreshCw, Check, Clock, User } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { playClick, playSuccess } from '@/utils/sounds';
import { AvatarCanvasSmall } from './AvatarCanvas';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SlotLinkQR({ 
  team, 
  slotIndex, 
  teamName,
  authHeader,
  onClaimed,
  onClose,
  onFallbackLogin 
}) {
  const [linkSession, setLinkSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeLeft, setTimeLeft] = useState(600); // 10 minutes in seconds
  const [claimed, setClaimed] = useState(false);
  const [claimedUser, setClaimedUser] = useState(null);

  // Create link session on mount
  useEffect(() => {
    createLinkSession();
  }, []);

  // Polling for status updates
  useEffect(() => {
    if (!linkSession || claimed) return;

    const pollInterval = setInterval(async () => {
      try {
        const response = await axios.get(
          `${API}/local-link/status/${linkSession.code}`,
          { headers: authHeader }
        );
        
        if (response.data.claimed) {
          setClaimed(true);
          setClaimedUser(response.data.claimed_by);
          playSuccess();
          
          // Notify parent
          if (onClaimed) {
            onClaimed({
              team,
              slotIndex,
              user: response.data.claimed_by,
            });
          }
        }
        
        if (response.data.expired) {
          setError('Session expired');
          clearInterval(pollInterval);
        }
      } catch (err) {
        // Ignore polling errors
      }
    }, 2000); // Poll every 2 seconds

    return () => clearInterval(pollInterval);
  }, [linkSession, claimed, authHeader, onClaimed, team, slotIndex]);

  // Countdown timer
  useEffect(() => {
    if (!linkSession || claimed) return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          setError('Session expired');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [linkSession, claimed]);

  const createLinkSession = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.post(
        `${API}/local-link/create`,
        { team, slot_index: slotIndex, team_name: teamName },
        { headers: authHeader }
      );
      
      setLinkSession(response.data);
      setTimeLeft(600); // Reset timer
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create link session');
      toast.error('Failed to create link code');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    playClick();
    setClaimed(false);
    setClaimedUser(null);
    createLinkSession();
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Render claimed state
  if (claimed && claimedUser) {
    return (
      <div 
        className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <div 
          className="bg-cream rounded-lg border-4 border-grass p-6 max-w-sm w-full text-center"
          onClick={(e) => e.stopPropagation()}
          data-testid="slot-link-claimed"
        >
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-grass flex items-center justify-center">
              <Check className="text-cream" size={32} />
            </div>
          </div>
          
          <h2 className="retro-heading text-lg text-ink mb-2">SLOT LINKED!</h2>
          
          <div className="flex items-center justify-center gap-3 my-4">
            {claimedUser.avatar_config && (
              <AvatarCanvasSmall config={claimedUser.avatar_config} size={48} />
            )}
            <div className="text-left">
              <p className="retro-heading text-sm text-grass">{claimedUser.username}</p>
              <p className="retro-text text-xs text-dirt">joined {teamName || `Team ${team}`}</p>
            </div>
          </div>
          
          <button
            onClick={() => { playClick(); onClose(); }}
            className="pixel-btn pixel-btn-primary w-full mt-4"
            data-testid="close-claimed-btn"
          >
            DONE
          </button>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="bg-cream rounded-lg border-4 border-ink p-6 max-w-sm w-full"
        onClick={(e) => e.stopPropagation()}
        data-testid="slot-link-qr-modal"
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h2 className="retro-heading text-lg text-ink">LINK ACCOUNT</h2>
          <button
            onClick={() => { playClick(); onClose(); }}
            className="p-1 hover:bg-ink/10 rounded"
            data-testid="close-qr-modal-btn"
          >
            <X size={20} className="text-ink" />
          </button>
        </div>

        {/* Slot info */}
        <div className="bg-grass/10 p-3 rounded-lg border-2 border-grass mb-4 text-center">
          <p className="retro-text text-sm text-ink">
            {teamName || `Team ${team}`} • Player {slotIndex + 1}
          </p>
        </div>

        {loading ? (
          <div className="text-center py-8">
            <RefreshCw className="animate-spin mx-auto text-grass" size={32} />
            <p className="retro-text text-sm text-dirt mt-2">Generating code...</p>
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <p className="retro-text text-sm text-red-600 mb-4">{error}</p>
            <button
              onClick={handleRefresh}
              className="pixel-btn"
              data-testid="refresh-code-btn"
            >
              <RefreshCw size={16} className="inline mr-2" />
              TRY AGAIN
            </button>
          </div>
        ) : linkSession && (
          <>
            {/* QR Code */}
            <div className="flex justify-center mb-4">
              <div className="bg-white p-4 rounded-lg border-2 border-ink">
                <QRCodeSVG 
                  value={linkSession.qr_url}
                  size={160}
                  level="M"
                  data-testid="qr-code"
                />
              </div>
            </div>

            {/* Instructions */}
            <p className="retro-text text-xs text-center text-dirt mb-4">
              Scan with your phone to link your account
            </p>

            {/* Divider */}
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1 h-px bg-ink/20" />
              <span className="retro-text text-xs text-dirt">OR ENTER CODE</span>
              <div className="flex-1 h-px bg-ink/20" />
            </div>

            {/* Join Code */}
            <div className="bg-ink p-4 rounded-lg text-center mb-4">
              <p className="retro-heading text-3xl text-cream tracking-widest" data-testid="join-code">
                {linkSession.code}
              </p>
            </div>

            {/* Timer */}
            <div className="flex items-center justify-center gap-2 text-dirt mb-4">
              <Clock size={14} />
              <span className="retro-text text-xs">
                Expires in {formatTime(timeLeft)}
              </span>
            </div>

            {/* Refresh button */}
            <button
              onClick={handleRefresh}
              className="pixel-btn w-full mb-3"
              data-testid="new-code-btn"
            >
              <RefreshCw size={14} className="inline mr-2" />
              NEW CODE
            </button>

            {/* Fallback manual login */}
            {onFallbackLogin && (
              <button
                onClick={() => { playClick(); onFallbackLogin(); }}
                className="w-full text-center retro-text text-xs text-dirt hover:text-ink underline"
                data-testid="fallback-login-btn"
              >
                or link manually with credentials
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
